import { waitForDomElement } from "./utils/dom-utils.js";

/* ==================================================
   PAGE HTML
================================================== */
export function BookEPCPage() {
  return `
<div class="hero">
  <div class="hero-content">
    <h1 class="hero-title">Request EPC Certificate</h1>
    <p class="hero-subtitle">
      Fill out the form and we will schedule a visit with the selected advisor
    </p>
  </div>
</div>

<section class="section bg-gray-50">
  <div class="container">
    <div class="max-w-6xl mx-auto">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">

        <!-- Left Column: Property Info -->
        <div class="lg:col-span-1">

          <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">📍 Property Information</h3>
            <div id="property-info-display" class="space-y-3 text-sm text-gray-600">
              <p>No property information available.</p>
            </div>
          </div>

          <!-- Advisor Info -->
          <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">👤 Selected Advisor</h3>
            <div id="advisor-info-display" class="space-y-3 text-sm text-gray-600">
              <p>No advisor selected.</p>
            </div>
          </div>
        </div>

        <!-- Right Column: Contact Form -->
        <div class="lg:col-span-2">
          <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-6">📞 Submit your request now</h3>

            <form id="booking-form" class="space-y-6">
              <input type="hidden" id="ts" name="ts">
              <!-- HONEYPOT -->
              <div style="position:absolute; left:-9999px; top:-9999px;">
                <input type="text" name="hp_field" tabindex="-1" autocomplete="off">
              </div>

              <div>
                <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2">
                  Full name *
                </label>
                <input type="text" id="firstName" name="firstName" required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>

              <div>
                <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
                  Phone number *
                </label>
                <input type="tel" id="phone" name="phone" required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>

              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                  Email address *
                </label>
                <input type="email" id="email" name="email" required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>

              <!-- Additional Information -->
              <div>
                <label for="additionalInfo" class="block text-sm font-medium text-gray-700 mb-2">
                  Additional information (optional)
                </label>
                <textarea id="additionalInfo" name="additionalInfo" rows="4"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"></textarea>
              </div>

              <!-- Privacy checkbox -->
              <div class="flex items-start">
                <input type="checkbox" id="privacy" name="privacy"
                  class="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                <label for="privacy" class="text-sm text-gray-700">
                  I agree to receive information, promotions, and commercial offers related to Certicalia products and services offered by third parties or by Certicalia in collaboration with third parties. By clicking the "Request now" button, you accept the general customer conditions, the <a href="/privacy/" class="text-blue-600 hover:underline">privacy policy</a> and the cookies policy.*
                </label>
              </div>

              <!-- Submit Button -->
              <div class="pt-4">
                <button type="submit" id="submit-booking"
                  class="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2">
                   Request now
                </button>
                <p class="text-xs text-gray-500 mt-3 text-center" id="pay-company-text">
                  You will not be charged now. Pay directly to <span id="pay-company-name"></span>
                </p>
              </div>
            </form>
          </div>
        </div>

      </div>
    </div>
  </div>
</section>

  `;
}

/* ==================================================
   INIT
================================================== */
export function initializeBookingPage() {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeBookingPage);
    return;
  }

  waitForDomElement("#property-info-display", loadPropertyInformation);
  waitForDomElement("#advisor-info-display", loadAdvisorInformation);

  // 🔥 JUISTE manier: callback gebruiken
  waitForDomElement("#pay-company-name", () => {
    let displayCompanyName = 'el asesor seleccionado';

    try {
      const advisorData = JSON.parse(
        sessionStorage.getItem('selectedAdvisorData')
      );

      if (
        advisorData &&
        advisorData.company &&
        advisorData.company !== 'Team EPC Spain Homes'
      ) {
        displayCompanyName = advisorData.company;
      }
    } catch (e) {
      console.warn('[BookingPage] selectedAdvisorData ongeldig');
    }

    const el = document.getElementById('pay-company-name');
    if (el) el.textContent = displayCompanyName;
  });

  setupFormSubmission();
}



/* ==================================================
   PROPERTY
================================================== */
function loadPropertyInformation() {
  const el = document.getElementById("property-info-display");
  if (!el) return;

  let data;
  try {
    data = JSON.parse(sessionStorage.getItem("bookingPropertyData") || "");
  } catch {
    data = null;
  }

  if (!data) {
    el.innerHTML = `<p class="text-gray-500">No property data available.</p>`;
    return;
  }

  el.innerHTML = `
<table class="w-full text-sm">
  <tr><td>Service type</td><td>${data.servicio || data.serviceType || "-"}</td></tr>
  <tr><td>Province</td><td>${data.provincia || "-"}</td></tr>
  <tr><td>Municipality</td><td>${data.municipios?.[0] || data.municipio || "-"}</td></tr>
  <tr>
  <td>Postal code</td>
  <td>${data.codigoPostal || data.codigo_postal || "-"}</td>
  <tr><td>Building type</td><td>${data.tipo || data.buildingType || "-"}</td></tr>
</tr>
  ${data.anoConstruccion ? `
<tr>
  <td>Year of construction</td>
  <td>${data.anoConstruccion}</td>
</tr>
` : ""}
  <tr><td>Surface</td><td>${data.superficie ? data.superficie + ' m2' : "-"}</td></tr>
  ${data.codigoRegistrales ? `
<tr>
  <td>Registry code</td>
  <td>${data.codigoRegistrales}</td>
</tr>
` : ""}

  <tr><td>      <a href="#" onclick="history.back(); return false;" class="text-blue-600 underline text-sm">
        Adjust data
      </a></td></tr>
</table>
`;
}

/* ==================================================
   ADVISOR
================================================== */
function loadAdvisorInformation() {
  const el = document.getElementById("advisor-info-display");
  if (!el) return;

  let advisor;
  try {
    advisor = JSON.parse(sessionStorage.getItem("selectedAdvisorData") || "");
  } catch {
    advisor = null;
  }

  if (!advisor) {
    el.innerHTML = `
      <p class="text-gray-500">No advisor selected.</p>
      <a href="/compare/" class="text-blue-600 underline text-sm">
        Choose advisor
      </a>
    `;
    return;
  }

  const price = advisor.matched_price?.price;
  const days = advisor.matched_price?.delivery_days ?? 5;

 const LOGO_BASE = "https://ceecasasespana.com";

const logoUrl = advisor.logo
  ? advisor.logo.startsWith("http")
    ? advisor.logo
    : `${LOGO_BASE}${advisor.logo}`
  : "https://ceecasasespana.com/logos/Logo.webp";

el.innerHTML = `
<div class="flex items-center gap-3 mb-3">
  <img src="${logoUrl}"
       class="w-12 h-12 object-contain">
  <strong>${advisor.company}</strong>
</div>

<div class="text-sm space-y-1">
  <div>Delivery: ${days} days</div>
  ${price ? `<div class="font-semibold">€${Number(price).toFixed(2)}</div>` : ""}
</div>
`;
}

function fillPayCompany(el) {
  try {
    const advisor = JSON.parse(sessionStorage.getItem("selectedAdvisorData") || "{}");
    el.textContent = advisor.company || "";
  } catch {
    el.textContent = "";
  }
}

// -------------------------------
// FORM SUBMISSION (BACKEND-COMPATIBEL)
// -------------------------------
function setupFormSubmission() {
  const form = document.getElementById("booking-form");
  if (!form) return;

  // 🔒 ts MOET OUDER DAN 3s ZIJN
  const tsField = document.getElementById("ts");
  if (tsField) {
    tsField.value = Date.now() - 5000; // 5 seconden geleden
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const submitButton = document.getElementById("submit-booking");
    const originalText = submitButton.textContent;

    submitButton.disabled = true;
    submitButton.textContent = "⏳ Sending request...";

    try {
      const formData = new FormData(form);

      // 🏠 Property data
      let propertyData = {};
      try {
        propertyData = JSON.parse(
          sessionStorage.getItem("bookingPropertyData") || "{}"
        );
      } catch {
        propertyData = {};
      }

      // 👤 Advisor data
      let advisorData = {};
      try {
        advisorData = JSON.parse(
          sessionStorage.getItem("selectedAdvisorData") || "{}"
        );
      } catch {
        advisorData = {};
      }

      const payload = {
        firstname: formData.get("firstName"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        additionalInfo: formData.get("additionalInfo"),

        propertyData,
        advisorData,

        // anti-bot
        hp_field: formData.get("hp_field") || "",
        ts: formData.get("ts"),
        timestamp: new Date().toISOString(),
        source: "website-booking-form"
      };

      // ✅ frontend validatie
      if (!payload.firstname || !payload.email || !payload.phone) {
        throw new Error("Please fill in all required fields");
      }

      console.log("📨 payload →", payload);

      const response = await fetch("/api/user_email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Error sending");
      }

      // 🧹 opruimen
      sessionStorage.removeItem("bookingPropertyData");
      sessionStorage.removeItem("selectedAdvisorData");

      form.innerHTML = `
        <div class="text-center py-8">
          <h3 class="text-xl font-semibold text-gray-900 mb-2">
            ✅ Request sent!
          </h3>
          <p class="text-gray-600 mb-4">
            We will contact you shortly.
          </p>
          <button onclick="location.href='/'"
            class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg">
            Back to home
          </button>
        </div>
      `;
    } catch (error) {
      alert(error.message || "Error sending the form");
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  });
}
